import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Gift,
  Infinity as InfinityIcon,
  KeyRound,
  Mail,
  Plus,
  Power,
  Wallet,
} from 'lucide-react';
import { functions } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { Badge, Button, Input, Modal, Spinner, cx } from './ui';

interface Member {
  uid: string;
  email: string;
  displayName: string;
  credits: number;
}

interface LabRow {
  code: string;
  name: string;
  organisation: string;
  active: boolean;
  /** On the house: nobody in this lab is charged for extraction. */
  comped: boolean;
  createdAt: string | null;
  members: Member[];
  credits: number;
}

type CreateResult = {
  ok: true;
  code: string;
  name: string;
  email: string;
  sent: boolean;
  reason?: string;
};

const callCreate = httpsCallable<
  { name: string; organisation: string; email?: string },
  CreateResult
>(functions, 'createLabCode');
const callList = httpsCallable<void, { ok: true; labs: LabRow[]; unaffiliated: Member[] }>(
  functions,
  'listLabs',
);
const callSetActive = httpsCallable<{ code: string; active: boolean }, { ok: true }>(
  functions,
  'setLabActive',
);
const callSetComped = httpsCallable<{ code: string; comped: boolean }, { ok: true }>(
  functions,
  'setLabComped',
);
const callGrant = httpsCallable<
  { uid: string; amount: number; note?: string },
  { ok: true; balance: number }
>(functions, 'grantAccountCredits');

const pretty = (code: string) => `${code.slice(0, 4)} ${code.slice(4)}`;

export default function LabAdmin({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const [labs, setLabs] = useState<LabRow[] | null>(null);
  const [unaffiliated, setUnaffiliated] = useState<Member[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [grantFor, setGrantFor] = useState<string | null>(null);
  const [grantAmount, setGrantAmount] = useState('150');
  const [name, setName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<CreateResult | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await callList();
      setLabs(res.data.labs);
      setUnaffiliated(res.data.unaffiliated ?? []);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not load lab codes.');
      setLabs([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setIssued(null);
    setError(null);
    setEmail(user?.email ?? '');
    void refresh();
  }, [open, refresh, user?.email]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    setIssued(null);
    try {
      const res = await callCreate({
        name: name.trim(),
        organisation: organisation.trim(),
        email: email.trim() || undefined,
      });
      setIssued(res.data);
      setName('');
      setOrganisation('');
      await refresh();
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not create the code.');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(row: LabRow) {
    try {
      await callSetActive({ code: row.code, active: !row.active });
      await refresh();
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not update that code.');
    }
  }

  async function toggleComped(row: LabRow) {
    try {
      await callSetComped({ code: row.code, comped: !row.comped });
      await refresh();
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not change that subscription.');
    }
  }

  async function grant(uid: string) {
    const amount = Number(grantAmount);
    if (!Number.isFinite(amount) || amount === 0) return;
    try {
      await callGrant({ uid, amount });
      setGrantFor(null);
      await refresh();
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not grant those credits.');
    }
  }

  /** One member line, with the credit controls that belong to it. */
  function MemberLine({ m }: { m: Member; }) {
    return (
      <li className="flex flex-wrap items-center gap-2 rounded-md bg-ink-900/60 px-2.5 py-1.5">
        <span className="min-w-0 flex-1 truncate text-xs text-ink-300">
          {m.email || m.displayName || m.uid}
        </span>
        {grantFor === m.uid ? (
          <span className="flex items-center gap-1.5">
            <Input
              type="number"
              value={grantAmount}
              onChange={(e) => setGrantAmount(e.target.value)}
              className="h-7 w-20 px-2 py-0 text-xs"
              aria-label="Credits to add"
              autoFocus
            />
            <Button size="sm" variant="primary" onClick={() => void grant(m.uid)}>
              Add
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setGrantFor(null)}>
              Cancel
            </Button>
          </span>
        ) : (
          <button
            onClick={() => {
              setGrantFor(m.uid);
              setGrantAmount('150');
            }}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs tabular-nums text-ink-300 hover:bg-ink-800 hover:text-ink-100"
            title="Add or remove credits"
          >
            <Wallet size={12} />
            {m.credits}
          </button>
        )}
      </li>
    );
  }

  function copy(code: string) {
    navigator.clipboard?.writeText(code).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      },
      () => setError('Could not copy — select the code and copy it manually.'),
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Lab access codes" wide>
      <p className="mb-4 text-sm text-ink-400">
        Each lab gets an 8-digit code. Members enter it once, then sign in. A lab marked free
        extracts without limit and is never charged; everyone else spends credits — 1 for a
        screen, 4 for a full sheet.
      </p>

      {issued && (
        <div className="mb-5 rounded-xl border border-emerald-800 bg-emerald-950/40 p-4">
          <p className="text-sm font-medium text-emerald-100">{issued.name} — code issued</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="font-mono text-3xl font-bold tracking-widest text-accent-400">
              {pretty(issued.code)}
            </span>
            <Button
              size="sm"
              variant="secondary"
              icon={copied ? <Check size={13} /> : <Copy size={13} />}
              onClick={() => copy(issued.code)}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-200/90">
            <Mail size={12} />
            {issued.sent
              ? `Emailed to ${issued.email}`
              : (issued.reason ?? 'Not emailed — copy the code above.')}
          </p>
        </div>
      )}

      <form onSubmit={create} className="mb-6 space-y-3 rounded-xl border border-ink-700 p-4">
        <p className="field-label">Issue a new code</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Lab name"
            autoFocus
          />
          <Input
            value={organisation}
            onChange={(e) => setOrganisation(e.target.value)}
            placeholder="Organisation (optional)"
          />
        </div>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Send the code to…"
        />
        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={busy}
          disabled={!name.trim()}
          icon={<Plus size={16} />}
        >
          Generate and email
        </Button>
      </form>

      {error && (
        <p className="mb-4 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">
          {error}
        </p>
      )}

      <p className="field-label mb-2">Labs and subscriptions</p>
      {labs === null ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : labs.length === 0 ? (
        <p className="rounded-lg border border-dashed border-ink-700 px-4 py-6 text-center text-sm text-ink-400">
          No codes yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {labs.map((row) => (
            <li
              key={row.code}
              className={cx(
                'rounded-lg border',
                row.active ? 'border-ink-700 bg-ink-800/40' : 'border-ink-800 bg-ink-900 opacity-60',
              )}
            >
              <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                <button
                  onClick={() => setExpanded(expanded === row.code ? null : row.code)}
                  className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-ink-100"
                  aria-label={expanded === row.code ? 'Hide accounts' : 'Show accounts'}
                >
                  {expanded === row.code ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <span className="font-mono text-sm font-semibold tracking-wider text-accent-400">
                  {pretty(row.code)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{row.name}</p>
                  <p className="truncate text-xs text-ink-400">
                    {row.organisation ? row.organisation + ' · ' : ''}
                    {row.members.length} {row.members.length === 1 ? 'account' : 'accounts'}
                    {!row.comped && ' · ' + row.credits + ' credits'}
                  </p>
                </div>
                {row.comped ? (
                  <Badge tone="good">
                    <InfinityIcon size={11} /> free
                  </Badge>
                ) : (
                  <Badge tone="info">pay as you go</Badge>
                )}
                {!row.active && <Badge tone="neutral">inactive</Badge>}
                <button
                  onClick={() => void toggleComped(row)}
                  className={cx(
                    'icon-btn',
                    row.comped
                      ? 'text-emerald-400 hover:bg-ink-800'
                      : 'text-ink-400 hover:bg-emerald-950 hover:text-emerald-300',
                  )}
                  aria-label={row.comped ? 'Start charging this lab' : 'Make this lab free'}
                  title={row.comped ? 'Start charging this lab' : 'Put this lab on the house'}
                >
                  <Gift size={14} />
                </button>
                <button
                  onClick={() => copy(row.code)}
                  className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-ink-100"
                  aria-label="Copy this code"
                >
                  <Copy size={14} />
                </button>
                <button
                  onClick={() => void toggle(row)}
                  className={cx(
                    'icon-btn',
                    row.active
                      ? 'text-ink-400 hover:bg-red-950 hover:text-red-300'
                      : 'text-emerald-400 hover:bg-emerald-950',
                  )}
                  aria-label={row.active ? 'Deactivate this code' : 'Reactivate this code'}
                  title={row.active ? 'Deactivate' : 'Reactivate'}
                >
                  <Power size={14} />
                </button>
              </div>

              {expanded === row.code && (
                <div className="border-t border-ink-800 px-3 py-2">
                  {row.comped && (
                    <p className="mb-2 text-xs text-emerald-300/80">
                      On the house — nobody here is charged, and the balances below are ignored.
                    </p>
                  )}
                  {row.members.length === 0 ? (
                    <p className="py-1 text-xs text-ink-400">
                      Nobody has signed in with this code yet.
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {row.members.map((m) => (
                        <MemberLine key={m.uid} m={m} />
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {unaffiliated.length > 0 && (
        <>
          <p className="field-label mb-2 mt-5">No lab code</p>
          <p className="mb-2 text-xs text-ink-400">
            Signed up on their own, so they buy their own credits. Granting some here is how a
            bank transfer, or an apology, gets settled.
          </p>
          <ul className="space-y-1">
            {unaffiliated.map((m) => (
              <MemberLine key={m.uid} m={m} />
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}

/** Entry point on the landing page. Renders nothing for non-admins. */
export function LabAdminButton() {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);

  if (!isAdmin) return null;

  return (
    <>
      <Button variant="ghost" icon={<KeyRound size={16} />} onClick={() => setOpen(true)}>
        Lab codes
      </Button>
      <LabAdmin open={open} onClose={() => setOpen(false)} />
    </>
  );
}
